import React from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ScanCreditsPanel } from "./ScanCredits";
import {
  specsheetBatchError,
  specsheetScanPhotoAIError,
  SCAN_GUIDED_REASONS,
  SCAN_NO_CREDITS,
  SCAN_SPEND_CAPS,
} from "./specsheet.redux";

/**
 * "We did not read your sheet, and here is what to do about it."
 *
 * Mounted once for the whole application, beside the support widget, and on
 * purpose: a scan can be started from a bowler's sheet, from the image
 * manager or from the inbox, and a refusal has to land somewhere on all of
 * them. It used to live inside the spec sheet, which meant the one component
 * that could explain the paywall was mounted only on the screens that happened
 * to contain a spec sheet.
 *
 * Two refusals, two different answers, and the difference matters more than it
 * looks:
 *
 * - Out of sheets. The shop can fix this itself, so the dialog is the shop:
 *   balance, packs, prices, buy.
 * - The monthly AI ceiling. The shop can do nothing about this and its sheets
 *   are untouched, so it must NOT be shown a till. Selling sheets to someone
 *   whose scanning is paused would take money for something that still would
 *   not work. It gets the explanation and a way to reach us.
 *
 * Everything else that can go wrong with a scan stays a toast.
 */

const StyledModal = styled(Modal)`
  .modal-content {
    background-color: #ffffff;
    box-shadow: inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border-radius: ${(p) => p.theme.radius.md};
    border: none;
  }
  .modal-header {
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    padding: calc(${(p) => p.theme.spacing.md} + 4px)
      ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.md};
  }
  .modal-body {
    padding: ${(p) => p.theme.spacing.md};
    overflow: visible;
  }
  .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  }
`;

const ModalTitle = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeLg};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => p.theme.color.primary};
  line-height: 1.2;
`;

const Bare = styled.div`
  /* The panel brings its own card; inside a dialog body that would be a box
     in a box. */
  > div {
    border: none;
    background: transparent;
    padding: 0;
    margin: 0;
  }
`;

/* What the server said, first and in the operator's words, before our
   explanation of it. */
const Lead = styled.p`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => p.theme.color.text};
  margin: 0 0 12px;
`;

const Body = styled.p`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.textMuted};
  line-height: 1.5;
  margin: 0;

  a {
    color: ${(p) => p.theme.color.primary};
    font-weight: ${(p) => p.theme.font.weightSemibold};
  }
`;

const ScanLimitDialog = ({ message, reason, source, onClear }) => {
  const { t } = useTranslation();

  const capped = SCAN_SPEND_CAPS.indexOf(reason) !== -1;
  const show = !!message && (capped || reason === SCAN_NO_CREDITS);
  const onHide = () => onClear(source);

  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <ModalTitle>
          {t(
            capped
              ? "specsheetScan.credits.pausedTitle"
              : "specsheetScan.credits.outTitle",
          )}
        </ModalTitle>
      </Modal.Header>
      <Modal.Body>
        {capped ? (
          <>
            <Lead>{message}</Lead>
            <Body>
              {t("specsheetScan.credits.pausedBody")}{" "}
              <a href="mailto:support@spectrebowling.com">
                support@spectrebowling.com
              </a>
            </Body>
          </>
        ) : (
          <Bare>
            <ScanCreditsPanel refusedMessage={message} />
          </Bare>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          {t("specsheetScan.batch.close")}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

const guided = (reason) => SCAN_GUIDED_REASONS.indexOf(reason) !== -1;

/**
 * A single scan and a batch upload keep their refusals in different places,
 * because they are different screens' state. The dialog is the same either
 * way, so whichever one refused is the one it shows.
 */
const mapState = (state) => {
  const s = state.specsheet;
  const single = guided(s.scanErrorReason) ? s.scanErrorReason : "";
  const batch = guided(s.batchErrorReason) ? s.batchErrorReason : "";

  return {
    reason: single || batch,
    message: single ? s.scanError : batch ? s.batchError : "",
    source: single ? "single" : batch ? "batch" : "",
  };
};

/**
 * Only the refusal being shown is cleared. Clearing both looks tidier and is
 * wrong: the batch action also resets the inbox's upload counters, so closing
 * a single scan's dialog would wipe the progress of a batch still uploading
 * behind it.
 */
const mapDispatch = (dispatch) => ({
  onClear: (source) =>
    dispatch(
      (source === "batch" ? specsheetBatchError : specsheetScanPhotoAIError)({
        message: "",
        reason: "",
      }),
    ),
});

export const ScanLimitDialogContainer = connect(
  mapState,
  mapDispatch,
)(ScanLimitDialog);

export default ScanLimitDialogContainer;
